import type { SvgIconComponent } from "@mui/icons-material";
import { Alert, Button, DialogContent, DialogTitle, Stack, TextField, Typography } from "@mui/material";
import { useCallback, useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import RequiredFieldMarker from "@entrilia/shared/components/RequiredFieldMarker";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import useDefinedContext from "@entrilia/shared/hooks/useDefinedContext";
import useFetch from "@entrilia/shared/hooks/useFetch";
import ApproveRequestDialogIcon from "@entrilia/shared/icons/ApproveRequestDialogIcon";
import DeleteDialogIcon from "@entrilia/shared/icons/DeleteDialogIcon";
import RejectRequestDialogIcon from "@entrilia/shared/icons/RejectRequestDialogIcon";
import RevertDialogIcon from "@entrilia/shared/icons/RevertDialogIcon";
import SubmitRequestDialogIcon from "@entrilia/shared/icons/SubmitRequestDialogIcon";
import { logError } from "@entrilia/shared/logging";
import { except } from "@entrilia/shared/utilities/arrayHelper";
import { pluralize } from "@entrilia/shared/utilities/stringHelper";
import adminApi from "../../../../api/adminApi";
import type { ApproverAssignment } from "../../../../api/types/approvalTypes";
import type { ObjectFieldValues } from "../../../../api/types/objectTypes";
import type { WorkflowApproval } from "../../../../api/types/workflowTypes";
import useClientContext from "../../../../context/useClientContext";
import ActionChecksAlert from "../../../approvals/workflows/ActionChecksAlert";
import ApproversAssignmentDialog from "../../../approvals/workflows/ApproversAssignmentDialog";
import { getInitialApproversAssignmentStateFromApprovalPreview } from "../../../approvals/workflows/approversAssignmentState";
import ObjectsListContext from "../../../entityFields/grid/ObjectsListContext";
import { duplicateWarningActions } from "../../../entityFields/objectDuplicates";
import type { InvoiceAction } from "./invoiceGridActions";
import { getInvoiceIdsForAction } from "./invoiceGridActions";
import InvoicesGridContext from "./InvoicesGridContext";

const getInvoiceApproversAssignment = withErrorHandling(adminApi.getInvoiceApproversAssignment);
const executeInvoiceAction = withErrorHandling(adminApi.executeInvoiceAction);

type CommentMode = "none" | "optional" | "required";

interface ActionCopy {
  title: string;
  verb: string;
  Icon: SvgIconComponent;
  pastParticiple: string;
  getPrompt: (subject: string) => string;
  getDescription: (subject: string) => string;
  commentMode: CommentMode;
  confirmColor: "primary" | "error";
  confirmVariant: "contained" | "outlined";
}

const actionCopies: Record<InvoiceAction, ActionCopy> = {
  SubmitInvoiceForApproval: {
    title: "Submit for Approval",
    verb: "submit",
    Icon: SubmitRequestDialogIcon,
    pastParticiple: "submitted for approval",
    getPrompt: (subject) => `Submit ${subject} for approval?`,
    getDescription: (subject) => `You're about to submit ${subject} for approval.`,
    commentMode: "none",
    confirmColor: "primary",
    confirmVariant: "contained",
  },
  Approve: {
    title: "Approve",
    verb: "approve",
    Icon: ApproveRequestDialogIcon,
    pastParticiple: "approved",
    getPrompt: (subject) => `Approve ${subject}?`,
    getDescription: (subject) => `You're about to approve ${subject}.`,
    commentMode: "optional",
    confirmColor: "primary",
    confirmVariant: "contained",
  },
  Reject: {
    title: "Reject",
    verb: "reject",
    Icon: RejectRequestDialogIcon,
    pastParticiple: "rejected",
    getPrompt: (subject) => `Reject ${subject}?`,
    getDescription: (subject) => `You're about to reject ${subject}.`,
    commentMode: "required",
    confirmColor: "error",
    confirmVariant: "outlined",
  },
  ReturnInvoiceToDraft: {
    title: "Return to Draft",
    verb: "return to draft",
    Icon: RevertDialogIcon,
    pastParticiple: "returned to draft",
    getPrompt: (subject) => `Return ${subject} to draft?`,
    getDescription: (subject) => `You're about to return ${subject} to draft.`,
    commentMode: "none",
    confirmColor: "primary",
    confirmVariant: "contained",
  },
  DeleteInvoice: {
    title: "Delete",
    verb: "delete",
    Icon: DeleteDialogIcon,
    pastParticiple: "deleted",
    getPrompt: (subject) => `Delete ${subject}?`,
    getDescription: (subject) => `You're about to delete ${subject}. This action is permanent and can't be undone.`,
    commentMode: "none",
    confirmColor: "error",
    confirmVariant: "outlined",
  },
};

const approveWayOut = "Fix these, then approve. If you can't, reject it and say what needs fixing.";

interface Props {
  action: InvoiceAction;
  invoices: ObjectFieldValues[];
  selectedInvoiceCount?: number;
  onClose: () => void;
  onExecuted: (succeededInvoiceIds: string[]) => void;
}

const InvoiceActionDialog = ({ action, invoices, selectedInvoiceCount, onClose, onExecuted }: Props) => {
  const { sendNotification, sendNotificationError } = useNotificationContext();
  const { updateMenuBadges } = useClientContext();
  const { objectClassDefinition } = useDefinedContext(ObjectsListContext, "ObjectsListContext");
  const { refetchPendingApprovals, ...eligibility } = useDefinedContext(InvoicesGridContext, "InvoicesGridContext");

  const [executing, setExecuting] = useState(false);
  const [comment, setComment] = useState("");
  const [approvalPreview, setApprovalPreview] = useState<WorkflowApproval>();
  const [invoiceIdsToCheck] = useState(() =>
    objectClassDefinition.workflowActionsWithChecks?.includes(action)
      ? getInvoiceIdsForAction(invoices, action, eligibility)
      : []
  );

  const getChecks = useCallback(
    () => adminApi.getInvoiceActionChecks({ action, invoiceIds: invoiceIdsToCheck }),
    [action, invoiceIdsToCheck]
  );

  const [checksResponse, checksError, { isFetching: fetchingChecks }] = useFetch(getChecks, {
    disabled: invoiceIdsToCheck.length === 0,
  });

  if (checksError) {
    logError(checksError, "[InvoiceActionDialog] getInvoiceActionChecks");
  }

  const eligibleInvoiceIds = getInvoiceIdsForAction(invoices, action, eligibility);
  const getInvoicesWith = (kind: "blockers" | "warnings") =>
    invoices
      .map(({ id, fieldValues, displayValues }) => {
        const invoiceNo = fieldValues["InvoiceNo"];
        return {
          id,
          name:
            (typeof invoiceNo === "string" && invoiceNo) || displayValues.primaryValue || "Invoice without a number",
          messages: checksResponse?.invoices.find(({ invoiceId }) => invoiceId === id)?.[kind] ?? [],
        };
      })
      .filter(({ id, messages }) => messages.length > 0 && eligibleInvoiceIds.includes(id));
  const blockedInvoices = getInvoicesWith("blockers");
  const invoiceIdsToExecute = except(
    eligibleInvoiceIds,
    blockedInvoices.map(({ id }) => id)
  );
  const warnedInvoices = getInvoicesWith("warnings").filter(({ id }) => invoiceIdsToExecute.includes(id));
  const allInvoicesBlocked = blockedInvoices.length > 0 && invoiceIdsToExecute.length === 0;
  const promptInvoiceIds = allInvoicesBlocked ? eligibleInvoiceIds : invoiceIdsToExecute;
  const totalInvoiceCount = selectedInvoiceCount ?? invoices.length;
  const ineligibleInvoiceCount = totalInvoiceCount - eligibleInvoiceIds.length;
  const copy = actionCopies[action];
  const trimmedComment = comment.trim() || undefined;
  const isCommentMissing = copy.commentMode === "required" && trimmedComment === undefined;

  const flaggedInvoiceCount = invoices.filter(
    ({ id, hasPossibleDuplicates }) => hasPossibleDuplicates && invoiceIdsToExecute.includes(id)
  ).length;
  const showDuplicateWarning = duplicateWarningActions.includes(action) && flaggedInvoiceCount > 0;
  const duplicateWarningText = `${flaggedInvoiceCount} ${pluralize("invoice", flaggedInvoiceCount)} with possible duplicates`;

  const getPromptSubject = () =>
    promptInvoiceIds.length === 1
      ? invoices.find(({ id }) => id === promptInvoiceIds[0])?.displayValues.primaryValue || "invoice"
      : `${promptInvoiceIds.length} invoices`;

  const getBlockersTitle = () => {
    if (eligibleInvoiceIds.length === 1) {
      return `This invoice can't be ${copy.pastParticiple} yet`;
    }

    return allInvoicesBlocked
      ? `None of the ${eligibleInvoiceIds.length} invoices can be ${copy.pastParticiple} yet`
      : `${blockedInvoices.length} of the ${eligibleInvoiceIds.length} invoices can't be ${copy.pastParticiple} yet and will be skipped`;
  };

  const handleConfirm = async () => {
    if (action !== "SubmitInvoiceForApproval") {
      await executeAction();
      return;
    }

    setExecuting(true);
    const [assignment, error] = await getInvoiceApproversAssignment(invoiceIdsToExecute);
    if (error) {
      setExecuting(false);
      logError(error, "[InvoiceActionDialog] getInvoiceApproversAssignment");
      sendNotificationError(error.message);
      return;
    }

    if (!assignment.approvalPreview) {
      await executeAction();
      return;
    }

    setExecuting(false);
    setApprovalPreview(assignment.approvalPreview);
  };

  const executeAction = async (approverAssignments?: ApproverAssignment[]) => {
    setExecuting(true);
    const [response, error] = await executeInvoiceAction({
      action,
      invoiceIds: invoiceIdsToExecute,
      commentUserInput: trimmedComment,
      approverAssignments,
    });
    setExecuting(false);

    if (error) {
      logError(error, "[InvoiceActionDialog] executeInvoiceAction");
      sendNotificationError(error.message);
      return;
    }

    const failures = response.results.filter(({ isSuccess }) => !isSuccess);
    if (failures.length === 0) {
      sendNotification(
        invoiceIdsToExecute.length === 1
          ? `Invoice ${copy.pastParticiple}`
          : `${invoiceIdsToExecute.length} invoices ${copy.pastParticiple}`
      );
    } else {
      const reasons = [...new Set(failures.flatMap(({ errorMessage }) => errorMessage ?? []))].join(" ");
      const summary =
        invoiceIdsToExecute.length === 1
          ? `Could not ${copy.verb} the invoice.`
          : `Could not ${copy.verb} ${failures.length} of ${invoiceIdsToExecute.length} invoices. They remain selected.`;
      logError(new Error(reasons || summary), "[InvoiceActionDialog] executeInvoiceAction");
      sendNotificationError(reasons ? `${summary} ${reasons}` : summary);
    }

    onExecuted(response.results.filter(({ isSuccess }) => isSuccess).map(({ invoiceId }) => invoiceId));
    refetchPendingApprovals();
    updateMenuBadges();
    onClose();
  };

  const handleClose = () => {
    if (!executing) {
      onClose();
    }
  };

  if (approvalPreview) {
    return (
      <ApproversAssignmentDialog
        initialState={getInitialApproversAssignmentStateFromApprovalPreview(approvalPreview)}
        actionName={
          invoiceIdsToExecute.length === 1
            ? "Submit Invoice for Approval"
            : `Submit ${invoiceIdsToExecute.length} Invoices for Approval`
        }
        loading={executing}
        Subheader={
          showDuplicateWarning && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {duplicateWarningText}
            </Alert>
          )
        }
        onConfirm={executeAction}
        onClose={handleClose}
      />
    );
  }

  const { Icon } = copy;

  return (
    <SmallDialog open onClose={handleClose}>
      <DialogTitle>{copy.title}</DialogTitle>
      <DialogCloseButton onClick={handleClose} />
      <DialogContent>
        <Stack spacing={2} sx={{ alignItems: "center" }}>
          <Icon sx={{ width: 80, height: 80 }} />
          <Stack spacing={0.5} sx={{ alignItems: "center" }}>
            <Typography variant="h6" sx={{ textAlign: "center" }}>
              {copy.getPrompt(getPromptSubject())}
            </Typography>
            <Typography color="textSecondary" sx={{ textAlign: "center" }}>
              {copy.getDescription(promptInvoiceIds.length === 1 ? "this invoice" : "these invoices")}
            </Typography>
          </Stack>
          {ineligibleInvoiceCount > 0 && (
            <Alert severity="info" sx={{ width: "100%" }}>
              {ineligibleInvoiceCount} of the {totalInvoiceCount} selected invoices can&apos;t be {copy.pastParticiple}{" "}
              and will be skipped.
            </Alert>
          )}
          {checksError && (
            <Alert severity="info" sx={{ width: "100%" }}>
              We couldn&apos;t check for blockers.
            </Alert>
          )}
          {blockedInvoices.length > 0 && (
            <ActionChecksAlert
              title={getBlockersTitle()}
              groups={blockedInvoices}
              hint={action === "Approve" ? approveWayOut : undefined}
              showGroupNames={eligibleInvoiceIds.length > 1}
            />
          )}
          {warnedInvoices.length > 0 && (
            <ActionChecksAlert
              severity="warning"
              groups={warnedInvoices}
              showGroupNames={eligibleInvoiceIds.length > 1}
            />
          )}
          {showDuplicateWarning && (
            <Alert severity="warning" sx={{ width: "100%" }}>
              {duplicateWarningText}
            </Alert>
          )}
          {copy.commentMode !== "none" && (
            <TextField
              fullWidth
              multiline
              rows={5}
              label={
                copy.commentMode === "required" ? (
                  <Typography>
                    Comment
                    <RequiredFieldMarker />
                  </Typography>
                ) : (
                  "Comment (optional)"
                )
              }
              value={comment}
              disabled={executing}
              onChange={(e) => setComment(e.target.value)}
            />
          )}
        </Stack>
      </DialogContent>
      <DialogButtons onCancel={handleClose} cancelDisabled={executing}>
        <Button
          variant={copy.confirmVariant}
          color={copy.confirmColor}
          loading={executing || fetchingChecks}
          disabled={isCommentMissing || allInvoicesBlocked}
          onClick={handleConfirm}
        >
          {copy.title}
        </Button>
      </DialogButtons>
    </SmallDialog>
  );
};

export default InvoiceActionDialog;
